import usePrevState from '@hooks/usePrevState';
import { FavoritePrepaidCardRow } from '@models/prepaidCard';
import { useSetAtom } from 'jotai';
import {
  CSSProperties, useCallback, useEffect, useMemo, useState,
} from 'react';

import { currentCardAtom } from '../../../atoms/prepaidCardAtoms';

const MAX_TOP = 130;
const MIN_TOP = 64;
const FRONT_TOP = 176;

interface Props {
  cards: FavoritePrepaidCardRow[];
}

function useRolling({ cards }: Props) {
  const { length } = cards;
  const prevCards = usePrevState(cards);
  const [index, setIndex] = useState<number>(0);
  const [animating, setAnimating] = useState<boolean>(false);
  const setCurrent = useSetAtom(currentCardAtom);
  const zIndexList = useMemo(() => {
    const original = Array.from({ length }, (_, i) => length - i);
    for (let i = 0; i < index; i += 1) {
      const pop = original.pop();
      original.unshift(pop!);
    }
    return original;
  }, [length, index]);

  const move = (i: number) => {
    setIndex(i);
    setAnimating(true);
    const timeout = setTimeout(() => {
      setAnimating(false);
    }, 100);
    return () => clearTimeout(timeout);
  };

  const moveNext = () => {
    move((index + 1) % length);
  };
  const movePrev = () => {
    const newIndex = index - 1;
    if (newIndex < 0) {
      move(length - 1);
    } else {
      move(newIndex);
    }
  };

  const getStyle = useCallback((current: number): CSSProperties => {
    const zIndex = zIndexList[current];
    const isShow = zIndex === length;
    const scale = isShow ? 1 : 0.66;
    const opacity = (zIndex === 1 && animating) ? 0.6 : 1;
    const space = (MAX_TOP - MIN_TOP) / ((length - 2) || 1);
    // const top = (isShow ? FRONT_TOP : space * (zIndex - 1) + MIN_TOP);
    const maxTop = 26 + length * 30;
    const top = isShow ? maxTop : maxTop - (length - zIndex) * 30;
    const transform = `scale(${scale}) ${(animating && zIndex === 1) ? 'translateY(100px)' : ''}`;
    return {
      top,
      transform,
      zIndex,
      opacity,
    };
  }, [length, zIndexList, animating]);
  useEffect(() => {
    setCurrent(cards[index]);
  }, [cards, index]);

  // 카드가 변경될 때 index 초기화
  useEffect(() => {
    const prev = prevCards.map((it) => it.id).join(',');
    const current = cards.map((it) => it.id).join(',');
    if (prev !== current) {
      setIndex(0);
    }
  }, [prevCards, cards]);
  return {
    getStyle,
    move,
    index,
    moveNext,
    movePrev,
  };
}

export default useRolling;
